/**
 * 支付策略模块 - 统一支付逻辑
 * 支持 Alipay (CNY) 和 Stripe (USD) 两种支付方式
 */

import { message } from 'antd';
import { MutableRefObject } from 'react';

import { cancelUserCoupon } from '@/components/UIComponents/UserAccount/service';
import { UseTranslateType } from '@/hooks/useTranslate';
import {
  stripeSinglePay,
  stripeSinglePolling,
  stripeSubscribePay,
  stripeSubscribePolling,
} from '@/services/order';
import UserStore from '@/store/User';
import { AIFunction } from '@/utils/const';
import { fc_purchase_alert_expose } from '@/utils/eventlog/eventNewConfig';
import { ResponseProps } from '@/utils/request';

import { CurrencyType, OrderInfo, OrderType } from './index';

// 获取功能来源映射
export const getFeatureSource = (feature: AIFunction) => {
  const featureSourceMapping: { [key in AIFunction]?: number } = {
    [AIFunction.VideoTranslate]: 0,
    [AIFunction.FaceSwap]: 1,
    [AIFunction.Removal]: 2,
    [AIFunction.Avatar2]: 3,
    [AIFunction.TopUpgrade]: 6,
    [AIFunction.VoiceClone]: 7,
    [AIFunction.Me]: 9,
  };
  return featureSourceMapping[feature] ?? AIFunction.Other;
};

// 支付回调类型
export interface PaymentCallback {
  onSuccess: (orderId: string, data?: any) => void;
  onError: (msg: string) => void;
}

// 轮询回调类型
export interface PollingCallback {
  onSuccess: () => void;
  onError: (msg: 'timeout' | string) => void;
}

// 支付参数
export interface PaymentParams {
  feature: fc_purchase_alert_expose['from'];
  commodityCode: string;
  couponCode: string;
  currency: CurrencyType;
  orderType: OrderType;
}

// 轮询参数
export interface PollingParams {
  orderInfo: OrderInfo;
  t: UseTranslateType | undefined;
  pollPayKey: string;
  timer: MutableRefObject<NodeJS.Timeout | null>;
  callback: PollingCallback;
}

/**
 * 统一支付入口
 */
export const executePayment = async (
  params: PaymentParams,
  callback: PaymentCallback,
): Promise<string | void> => {
  return await stripeStrategy(params, callback);
};

/**
 * Stripe 策略
 * orderType === OrderType.Once  对应积分包或按月等一次性下单
 * orderType === OrderType.Combo 对应订阅套餐（按年等），额外支持查询未完成的订阅订单
 */
const stripeStrategy = async (
  params: PaymentParams,
  callback: PaymentCallback,
): Promise<void> => {
  const { feature = AIFunction.Other, commodityCode, couponCode, orderType } = params;

  const content = {
    channel: 'stripe',
    commodityCode: String(commodityCode),
    couponCode,
    redirectUrl: window.location.href,
    country: 'US',
    returnUrl: 'https://falcocut.ai/payTransform',
    supportDistribution: true,
  };

  if (orderType === OrderType.Combo) {
    try {
      const detail = await stripeSubscribePolling();
      const status = detail?.data?.status;

      if (status && status !== 'UNKNOWN' && status !== 'FAILED' && status !== 'CANCEL') {
        window.open(detail.data.redirectUrl, '_blank', 'noopener=yes,noreferrer=yes');
        return;
      }
    } catch (error) {
      // ignore detail error and continue creating new order
    }

    try {
      const res = await stripeSubscribePay(content);
      if (res.code === 200 && res.success) {
        callback.onSuccess(res.data.subscribeId);
        window.open(res.data.redirectUrl, '_blank', 'noopener=yes,noreferrer=yes');
      } else {
        callback.onError(res.msg || 'Stripe subscription payment failed');
      }
    } catch (err) {
      console.error('Stripe subscription payment error:', err);
      throw err;
    }
    return;
  }

  try {
    const res = await stripeSinglePay(content);
    if (res.code === 200 && res.success) {
      callback.onSuccess(res.data.orderId);
      window.open(res.data.webUrl, '_blank', 'noopener=yes,noreferrer=yes');
    } else {
      callback.onError(res.msg || 'Stripe single payment failed');
    }
  } catch (err) {
    console.error('Stripe single payment error:', err);
    throw err;
  }
};

/**
 * 统一轮询入口
 */
export const executePolling = (params: PollingParams): Promise<void> => {
  return stripePolling(params);
};

/**
 * Stripe 轮询
 */
const stripePolling = ({ orderInfo, t, pollPayKey, timer, callback }: PollingParams) => {
  return new Promise<void>((resolve, reject) => {
    const MAX_ATTEMPTS = 120;
    const WARNING_ATTEMPTS = 60;
    const isCombo = orderInfo.orderType === OrderType.Combo;
    const normalizeStatus = (response: ResponseProps) => {
      if (!response || typeof response !== 'object') return undefined;
      if (isCombo) {
        const status = (response.data as any)?.status;
        return typeof status === 'string' ? status.toLowerCase() : undefined;
      }
      return typeof response.data === 'string' ? response.data.toLowerCase() : undefined;
    };

    const poll = async (attempts: number) => {
      timer.current && clearTimeout(timer.current);

      try {
        if (attempts > MAX_ATTEMPTS) {
          timer.current && clearTimeout(timer.current);
          callback.onError('timeout');
          return resolve();
        }

        const params = isCombo
          ? { subscribeId: orderInfo.orderId }
          : { orderId: orderInfo.orderId };
        const data = isCombo
          ? await stripeSubscribePolling(params)
          : await stripeSinglePolling(params);

        const status = normalizeStatus(data);
        if (data.success && status === 'success') {
          callback.onSuccess();
          timer.current && clearTimeout(timer.current);
          return resolve();
        }

        timer.current = setTimeout(() => poll(attempts + 1), 2000);

        if (attempts === WARNING_ATTEMPTS) {
          message.open({
            key: pollPayKey,
            type: 'warning',
            content: t && t('merror.sub.noresult.toast'),
            duration: 3,
          });
        }
      } catch (error) {
        timer.current && clearTimeout(timer.current);
        callback.onError((error as any).message || 'Polling failed');
        return reject(error);
      }
    };

    if (!orderInfo.orderId) {
      return resolve();
    }

    poll(0);
  });
};

/**
 * 超时后取消优惠券占用
 */
const handleCouponCancelOnTimeout = (commodityCode: string) => {
  const couponInfo = UserStore.getState().couponInfo;

  if (couponInfo.type === 'ticket' && couponInfo.availabled?.length) {
    for (const item of couponInfo.availabled) {
      if (item.applicableGoods.includes(commodityCode)) {
        cancelUserCoupon(item.couponCode);
      }
    }
  }
};

/**
 * 获取适用的优惠券
 */
export const getApplicableCoupon = (commodityCode: string): string => {
  const couponInfo = UserStore.getState().couponInfo;

  if (couponInfo.type === 'ticket' && couponInfo.availabled?.length) {
    const result = couponInfo.availabled.find((item) =>
      item.applicableGoods.includes(commodityCode),
    );
    if (result) {
      return result.couponCode;
    }
  }

  return '';
};
